<template>
    <div class="custom-field-suggest">
        <h5
            v-if="title"
            class="custom-field-suggest__title"
        >
            {{ title }}
        </h5>
        <div class="custom-field-suggest__main">
            <PtIcon
                v-if="loading"
                class="custom-field-suggest__main-loading"
                icon="SolidLoadingBars"
            />
            <div
                v-else-if="nodata"
                class="custom-field-suggest__main-nodata"
            >
                <PtIcon
                    class="custom-field-suggest__main-nodata-icon"
                    icon="SolidNoDataTip"
                />
                <span class="custom-field-suggest__main-nodata-label">{{ $t('smartSearch.nodata') }}</span>
            </div>
            <div v-else>
                <div>
                    <ul
                        v-if="isOptionField"
                        class="custom-field-suggest__main-option"
                    >
                        <li
                            v-for="item in displayList"
                            :key="item.option_id"
                            class="custom-field-suggest__main-option-item"
                            @click="onSelectField(item)"
                        >
                            <span v-html="getHighlightName(item.option_text)" />
                        </li>
                    </ul>

                    <PtVirtualTree
                        v-else-if="isLayerField"
                        ref="tree"
                        class="custom-field-suggest__main-layer"
                        node-key="id"
                        item-class="custom-field-suggest__main-layer-node"
                        :data="displayList"
                        :default-expand-all="true"
                        :item-min-item-size="32"
                        :item-size-dependencies="() => ['height']"
                        :filter="stem"
                        :filter-node-method="filterLayerNode"
                    >
                        <template
                            #default="{
                                node,
                                data,
                                isExpand,
                                isShowExpandIcon,
                                toggleNodeExpand,
                            }"
                        >
                            <div
                                class="custom-field-suggest__main-layer-item"
                                :style="{ marginLeft: (node.level - 1) * 20 + 'px'}"
                            >
                                <div class="custom-field-suggest__main-layer-item__icon">
                                    <PtIcon
                                        v-if="isShowExpandIcon"
                                        class="item__icon parent-item"
                                        :icon="isExpand ? 'SolidDropdownOpen' : 'SolidDropdownRight'"
                                        @click="toggleNodeExpand(node)"
                                    />
                                    <PtIcon
                                        v-else
                                        class="item__icon child-item"
                                        icon="SolidSmallDot"
                                    />
                                </div>
                                <div
                                    class="custom-field-suggest__main-layer-item__label"
                                    @click="onSelectField(node.data)"
                                >
                                    <PtTextTruncate
                                        tag="span"
                                        :lines="2"
                                    >
                                        <span v-html="getHighlightName(data.name)" />
                                    </PtTextTruncate>
                                </div>
                            </div>
                        </template>
                        <template #empty>
                            <div
                                class="custom-field-suggest__main-nodata"
                            >
                                <PtIcon
                                    class="custom-field-suggest__main-nodata-icon"
                                    icon="SolidNoDataTip"
                                />
                                <span class="custom-field-suggest__main-nodata-label">{{ $t('smartSearch.nodata') }}</span>
                            </div>
                        </template>
                    </PtVirtualTree>

                    <ul
                        v-else
                        class="custom-field-suggest__main-common"
                    >
                        <li
                            v-for="item in displayList"
                            :key="item.value"
                            class="custom-field-suggest__main-common-item"
                            @click="onSelectField(item)"
                        >
                            <span
                                class="custom-field-suggest__main-common-item__value"
                                v-html="getHighlightName(item.value)"
                            />
                            <span
                                v-if="item.desc"
                                class="custom-field-suggest__main-common-item__desc"
                            >
                                {{ item.desc[lang] }}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import PtVirtualTree from '@patsnap-biz/virtual-tree';
import PtTextTruncate from '@patsnap-biz/text-truncate';
import PtIcon from '@patsnap-biz/icon';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import '@patsnap-ui/icon/assets/solid/NoDataTip.svg';
import '@patsnap-ui/icon/assets/solid/SmallDot.svg';
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/DropdownRight.svg';
import { messages } from '../../locales';

export default {
    name: 'CustomFieldsModal',
    i18n: {
        fallbackLocale: 'cn',
        messages,
    },
    components: {
        PtIcon,
        PtVirtualTree,
        PtTextTruncate,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: '',
        },
        stem: {
            type: String,
            default: '',
        },
        list: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            lang: _.get(this, '$i18n.locale', 'en'),
            /**
             * 搜索到的节点及其子节点
             */
            showKeys: [],
            expendNodeList: [],
        };
    },
    computed: {
        isOptionField() {
            return this.type === 'OPTION';
        },
        isLayerField() {
            return this.type === 'LAYER';
        },
        nodata() {
            return this.displayList.length === 0;
        },
        displayList() {
            const list = _.cloneDeep(this.list);
            if (this.isOptionField && this.stem) {
                return _.filter(list, (item) => _.includes(
                    (item.option_text || '').toLocaleUpperCase(),
                    this.stem.toLocaleUpperCase(),
                ));
            }
            return list;
        },

        treeToArray() {
            return this.treeToListByDFS(this.displayList);
        },
    },

    watch: {
        stem() {
            this.showKeys = [];
        },
        showKeys: _.debounce(function () {
            if (!this.showKeys.length) {
                this.$refs.tree.initExpandStatus();
            } else {
                this.expendNodeList = [];
                const filterList = _.filter(this.treeToArray, (item) => _.includes(
                    (item.name || '').toLocaleUpperCase(),
                    this.stem.toLocaleUpperCase(),
                ));
                this.getExpendNode(this.displayList);
                filterList.forEach((item) => {
                    if (!this.expendNodeList.includes(item.id)) {
                        this.$refs.tree.collapseNode({ key: item.id });
                    }
                });
            }
        }, 300),
    },

    created() {
        if (this.stem) {
            this.$nextTick(() => {
                this.$refs.tree.doFilter();
            });
        }
    },

    methods: {
        getHighlightName(name = '') {
            if (!_.trim(this.stem)) return name;
            const keyWordsReg = new RegExp(_.escapeRegExp(_.trim(this.stem)), 'gi');
            const highlightName = name.replace(
                keyWordsReg,
                (match) => `<span class="custom-field-high-color">${match}</span>`,
            );
            return highlightName;
        },

        filterLayerNode(node, filter) {
            const isFind = _.includes(
                (node.data.name || '').toLocaleUpperCase(),
                filter.toLocaleUpperCase(),
            );
            if (isFind || (node.parent && this.showKeys.includes(node.parent.key))) {
                this.showKeys.push(node.key);
                return true;
            }
            return false;
        },

        onSelectField(field) {
            let fieldValue = '';
            if (this.isOptionField) {
                fieldValue = _.get(field, 'option_text', '');
            } else if (this.isLayerField) {
                const parentNameList = _.get(field, 'parent_name_list', []);
                const fieldName = [_.get(field, 'name', '')];
                fieldValue = _.concat(parentNameList, fieldName).filter((item) => item).join('\\');
            } else {
                fieldValue = field.value;
            }

            this.$emit('onSelect', fieldValue);
        },

        getExpendNode(list, parentIdLIst = []) {
            _.forEach(list, (item) => {
                // 匹配到
                if (_.includes(
                    (item.name || '').toLocaleUpperCase(),
                    this.stem.toLocaleUpperCase(),
                )) {
                    this.expendNodeList = _.uniq(_.concat(this.expendNodeList, parentIdLIst));
                }

                if (item.children && item.children.length) {
                    const { id } = item;
                    const curIdList = [...parentIdLIst];
                    curIdList.push(id);
                    this.getExpendNode(item.children, curIdList);
                }
            });
        },

        treeToListByDFS(
            tree = [],
            childrenKeyString = 'children',
            selfKeyString = 'id',
            parentKeyString = 'parent',
            rootValueString = '-root',
        ) {
            const list = [];
            function deepUnfold(nodes, parentKey) {
                _.forEach(nodes, (item) => {
                    list.push(item);
                    // eslint-disable-next-line no-param-reassign
                    item[parentKeyString] = parentKey;
                    if (item[childrenKeyString] && item[childrenKeyString].length) {
                        deepUnfold(item[childrenKeyString], item[selfKeyString]);
                    }
                });
                return list;
            }
            return deepUnfold(tree, rootValueString);
        },
    },
};
</script>
